import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Switch, FormControlLabel, Typography, Snackbar, Alert,
  MenuItem, Paper, Divider,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Switch as MuiSwitch } from "@mui/material";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import AdminGridToolbar from "../../components/admin/AdminGridToolbar";
import AdminFilterPanel, { filterFieldSx } from "../../components/admin/AdminFilterPanel";
import AdminFormDialog from "../../components/admin/AdminFormDialog";
import { listSteps, createStep, updateStep, deleteStep, listProcesses } from "../../services/adminService";

const GRID_SX = {
  border: 0,
  "& .MuiDataGrid-columnHeaders": { backgroundColor: "rgba(0,0,0,0.03)", borderBottom: "1px solid rgba(0,0,0,0.12)" },
  "& .MuiDataGrid-row":           { borderBottom: "1px solid rgba(0,0,0,0.06)" },
  "& .MuiDataGrid-cell":          { outline: "none" },
};

const EMPTY = {
  process: "", step: "", description_step: "",
  is_mandatory: false, is_active: true, is_automatic: false,
  function_tx: "", url: "", soap_action: "", request_tag: "",
};

export default function EtapasPage() {
  const { t } = useTranslation();
  const [rows, setRows]               = useState([]);
  const [processes, setProcesses]     = useState([]);
  const [filterProcess, setFilter]    = useState("");
  const [search, setSearch]           = useState("");
  const [loading, setLoading]         = useState(false);
  const [rowSelectionModel, setRowSel]= useState({ type: "include", ids: new Set() });
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [deleteOpen, setDeleteOpen]   = useState(false);
  const [editing, setEditing]         = useState(null);
  const [form, setForm]               = useState(EMPTY);
  const [saving, setSaving]           = useState(false);
  const [snack, setSnack]             = useState({ open: false, msg: "", severity: "success" });

  const selectedIds = useMemo(() => Array.from(rowSelectionModel.ids), [rowSelectionModel]);
  const selectedRow = useMemo(() => rows.find(r => `${r.process}_${r.step}` === selectedIds[0]), [rows, selectedIds]);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await listSteps(filterProcess || undefined)); }
    catch (e) { showSnack(e.message, "error"); }
    finally { setLoading(false); }
  }, [filterProcess]);

  useEffect(() => { listProcesses().then(setProcesses).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      r.description_step?.toLowerCase().includes(q) ||
      r.function_tx?.toLowerCase().includes(q)
    );
  }, [rows, search]);

  const showSnack   = (msg, severity = "success") => setSnack({ open: true, msg, severity });
  const openNew     = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit    = () => { if (!selectedRow) return; setEditing(selectedRow); setForm({ ...EMPTY, ...selectedRow }); setDialogOpen(true); };
  const closeDialog = () => setDialogOpen(false);

  const handleSave = async () => {
    if (!form.process || form.step === "") return showSnack(t("admin.required.processStep"), "error");
    setSaving(true);
    try {
      if (editing) await updateStep(editing.process, editing.step, form);
      else         await createStep({ ...form, step: Number(form.step) });
      showSnack(editing ? t("admin.success.stepUpdated") : t("admin.success.stepCreated"));
      closeDialog();
      load();
    } catch (e) { showSnack(e.message, "error"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selectedIds.length) return;
    setSaving(true);
    try {
      const toDelete = rows.filter(r => selectedIds.includes(`${r.process}_${r.step}`));
      await Promise.all(toDelete.map(r => deleteStep(r.process, r.step)));
      showSnack(t("admin.success.stepDeleted"));
      setDeleteOpen(false);
      setRowSel({ type: "include", ids: new Set() });
      load();
    } catch (e) { showSnack(e.message, "error"); }
    finally { setSaving(false); }
  };

  const columns = [
    {
      field: "process", headerName: t("admin.col.processo"), width: 200,
      valueGetter: (_, row) => {
        const p = processes.find(p => p.process === row.process);
        return p ? `${p.process} · ${p.description_process}` : row.process;
      },
    },
    { field: "step",             headerName: t("admin.col.etapa"),         width: 80 },
    { field: "description_step", headerName: t("admin.col.descricao"),     flex: 1 },
    { field: "function_tx",      headerName: t("admin.col.funcaoTx"),      width: 220 },
    { field: "url",               headerName: "URL",                       width: 220 },
    { field: "soap_action",      headerName: "SOAPAction",                 width: 180 },
    { field: "request_tag",      headerName: "Request Tag",                width: 180 },
    { field: "is_active",    headerName: t("admin.col.ativaCol"),       width: 80,  renderCell: ({ value }) => <MuiSwitch checked={!!value} size="small" disableRipple sx={{ pointerEvents: "none" }} /> },
    { field: "is_automatic", headerName: t("admin.col.automaticaCol"), width: 100, renderCell: ({ value }) => <MuiSwitch checked={!!value} size="small" disableRipple sx={{ pointerEvents: "none" }} /> },
    { field: "is_mandatory", headerName: t("admin.col.obrigatoriaCol"), width: 105, renderCell: ({ value }) => <MuiSwitch checked={!!value} size="small" disableRipple sx={{ pointerEvents: "none" }} /> },
  ];

  const f  = (field) => ({ value: form[field] ?? "", onChange: e => setForm(p => ({ ...p, [field]: e.target.value })) });
  const sw = (field) => ({ checked: !!form[field], onChange: e => setForm(p => ({ ...p, [field]: e.target.checked })) });

  return (
    <Box sx={{ p: 2 }}>
      <AdminPageHeader title={t("admin.pages.etapas")} />

      <AdminFilterPanel>
        <TextField select label="Processo" size="small"
          value={filterProcess} onChange={e => setFilter(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ ...filterFieldSx, width: 240 }}>
          <MenuItem value="">{t("admin.filter.all")}</MenuItem>
          {processes.map(p => <MenuItem key={p.process} value={p.process}>{p.process} — {p.description_process}</MenuItem>)}
        </TextField>
        <TextField label="Descrição / Função TX" size="small"
          value={search} onChange={e => setSearch(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ ...filterFieldSx, width: 260 }} />
      </AdminFilterPanel>

      <Paper variant="outlined">
        <Divider />
        <DataGrid
          rows={filtered} columns={columns} getRowId={r => `${r.process}_${r.step}`}
          loading={loading} autoHeight checkboxSelection disableRowSelectionOnClick
          density="compact" showToolbar
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={setRowSel}
          slots={{ toolbar: AdminGridToolbar }}
          slotProps={{ toolbar: {
            title: t("admin.grid.etapas"),
            onNew: openNew, onEdit: openEdit, onDelete: () => setDeleteOpen(true),
            canEdit: selectedIds.length === 1, canDelete: selectedIds.length > 0,
          }}}
          sx={GRID_SX}
        />
      </Paper>

      <AdminFormDialog
        open={dialogOpen} onClose={closeDialog} onSave={handleSave} saving={saving}
        title={editing ? t("admin.dialog.editEtapa") : t("admin.dialog.newEtapa")}
      >
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField select label="Processo *" {...f("process")} disabled={!!editing} size="small"
            InputLabelProps={{ shrink: true }} sx={{ flex: 1 }}>
            {processes.map(p => (
              <MenuItem key={p.process} value={p.process}>
                {p.process}{p.description_process ? ` · ${p.description_process}` : ""}
              </MenuItem>
            ))}
          </TextField>
          <TextField label="Nº Etapa *" type="number" {...f("step")} disabled={!!editing} size="small"
            InputLabelProps={{ shrink: true }} sx={{ width: 120 }} />
        </Box>
        <TextField label="Descrição" {...f("description_step")} size="small"
          InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 255 }} />
        <TextField label="Função TX" {...f("function_tx")} size="small"
          InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 40 }}
          helperText={t("admin.helper.functionTx")} />
        <TextField label="URL" {...f("url")} size="small"
          InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 500 }}
          placeholder="http://pi-host/XISOAPAdapter/MessageServlet?senderService=...&interface=..." />
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField label="SOAPAction" {...f("soap_action")} size="small" sx={{ flex: 1 }}
            InputLabelProps={{ shrink: true }} placeholder="(padrão SAP)" />
          <TextField label="Request Tag" {...f("request_tag")} size="small" sx={{ flex: 1 }}
            InputLabelProps={{ shrink: true }} placeholder="ex: SimularFaturaRequest" />
        </Box>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {[
            { field: "is_active",    label: t("admin.label.ativa") },
            { field: "is_automatic", label: t("admin.label.automatica") },
            { field: "is_mandatory", label: t("admin.label.obrigatoria") },
          ].map(({ field, label }) => (
            <FormControlLabel
              key={field}
              control={<Switch {...sw(field)} />}
              label={label}
              componentsProps={{ typography: { sx: { fontFamily: "'72', Arial, sans-serif", fontSize: 13 } } }}
            />
          ))}
        </Box>
      </AdminFormDialog>

      <Dialog open={deleteOpen} onClose={() => !saving && setDeleteOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
        <DialogTitle sx={{ fontWeight: 600, fontSize: 14, pb: 1, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>{t("admin.dialog.confirmDelete")}</DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          <Typography sx={{ fontSize: 13, color: "text.secondary", lineHeight: 1.6 }}>
            {selectedIds.length > 1
              ? `Deletar ${selectedIds.length} etapas selecionadas?`
              : t("admin.confirm.deleteEtapa")}
          </Typography>
        </DialogContent>
        <Divider sx={{ opacity: 0.5 }} />
        <DialogActions sx={{ px: 2, py: 1.2, gap: 1 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={saving} sx={{ textTransform: "none", fontSize: 13 }}>{t("admin.buttons.cancel")}</Button>
          <Button onClick={handleDelete} disabled={saving} disableElevation sx={{ textTransform: "none", fontSize: 13, color: "#d32f2f", backgroundColor: "rgba(211,47,47,0.10)", "&:hover": { backgroundColor: "rgba(211,47,47,0.18)" }, borderRadius: 0 }}>
            {saving ? t("admin.buttons.deleting") : t("admin.buttons.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(p => ({ ...p, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(p => ({ ...p, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
